import { useEffect, useMemo, useState } from 'react'

import DialogTitle from '@components/mui/DialogTitle'
import {
	Autocomplete,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	Paper,
	Stack,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material'
import {
	DataGridPro,
	GridColDef,
	GridColumnHeaderParams,
	GridRenderCellParams,
} from '@mui/x-data-grid-pro'
import { DialogProps } from '@toolpad/core/useDialogs'
import Logger from '@utils/Logger'
import toast from 'react-hot-toast'

import ValueMappingButtons from './components/ValueMappingButtons/ValueMappingButtons'
import {
	fieldCategoryValues,
	fileLayoutStateToStr,
	getColumnMapsFromString,
	getStartRowFromString,
	getStringMapsFromString,
	parseExcelFile,
	StringMapObject,
} from './FileLayoutEditor.utils'

const log = Logger('FileLayoutDialog')

export default function FileLayoutEditor({
	payload,
	open,
	onClose,
}: DialogProps<{ file: File; string: string }, string | null>) {
	const theme = useTheme()
	const [excelData, setExcelData] = useState(null)
	const [rows, setRows] = useState<any[]>([])
	const [stringMaps, setStringMaps] = useState<StringMapObject>(
		getStringMapsFromString(payload.string)
	)
	const [startRow, setStartRow] = useState<number>(getStartRowFromString(payload.string))
	const [columnMaps, setColumnMaps] = useState<{ [key: string]: number }>(
		getColumnMapsFromString(payload.string)
	)
	const handleSaveFileLayout = () => {
		onClose(fileLayoutStateToStr(startRow, stringMaps, columnMaps))
	}

	const addNewColMap = (newColMap) => {
		const newValue = Object.values(newColMap)[0]
		setColumnMaps((prevMaps) => {
			// Remove key-value pair from columnMaps that shares the same value as the new map
			const filteredMaps = Object.entries(prevMaps).reduce((acc, [key, value]) => {
				if (value !== newValue) {
					acc[key] = value
				}
				return acc
			}, {})
			return { ...filteredMaps, ...newColMap }
		})
	}

	const deleteColMap = (index: number) => {
		setColumnMaps((prevMaps) => {
			const filteredMaps = Object.entries(prevMaps).reduce((acc, [key, value]) => {
				if (value !== index) {
					acc[key] = value
				}
				return acc
			}, {})
			return { ...filteredMaps }
		})
	}

	useEffect(() => {
		parseExcelFile(payload.file)
			.then((data) => {
				setExcelData(data)
				const rows = data.map((row, index) => {
					// Column/Row indexes start at 1 in final returned value
					const newRow = { id: index + 1 }
					row.forEach((item, colIndex) => {
						newRow[colIndex + 1] = item
					})
					return newRow
				})
				setRows(rows)
			})
			.catch((err) => {
				log.error(err)
				onClose(null)
				toast.error(`There was an issue reading the uploaded excel file:\n\n ${err}`, {
					duration: 5000,
				})
			})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [payload])

	const startRowColumn: GridColDef = useMemo(
		() => ({
			field: 'selectStartRow',
			renderHeader: () => (
				<Typography textAlign="center" variant="body2">
					<b>
						Start
						<br />
						Row
					</b>
				</Typography>
			),
			headerName: 'Start Row',
			align: 'center',
			headerAlign: 'center',
			width: 70,
			renderCell: (params: GridRenderCellParams) => (
				<Checkbox
					checked={startRow === params.id}
					onChange={(e) => {
						const { checked } = e.target
						return checked ? setStartRow(Number(params.id)) : setStartRow(null)
					}}
					sx={{
						'&.Mui-checked': {
							color: theme.palette.secondary.main,
						},
						p: 0,
					}}
				/>
			),
		}),
		[startRow, theme]
	)

	const categoryColumns: GridColDef[] = useMemo(() => {
		if (!excelData) return []
		// Allows for iterating over empty columns parsed from excel file
		// Finding the array with the longest length to create columns
		// (each array in excelData corresponds to an excel row)
		const columnsToMap = [...excelData.reduce((a, b) => (a.length > b.length ? a : b))]
		const columns: GridColDef[] = columnsToMap.map((column, index) => ({
			// Column/Row indexes start at 1 in final returned value
			field: `${index + 1}`,
			type: 'singleSelect',
			headerName: `${index + 1}`,
			width: 140,
			renderHeader: (params: GridColumnHeaderParams) => (
				<Stack justifyContent="flex-end" gap={2} sx={{ width: 140, height: '100%' }} mb={2}>
					{index === 0 && (
						<Typography sx={{ position: 'absolute', top: 10 }} variant="body2">
							<b>Table Headers</b>
						</Typography>
					)}
					<Autocomplete
						options={fieldCategoryValues}
						value={
							Object.keys(columnMaps).find((key) => columnMaps[key] === Number(params.field)) ||
							null
						}
						onChange={(e, newValue) => {
							if (!newValue) return deleteColMap(Number(params.field))
							addNewColMap({ [newValue]: Number(params.field) })
						}}
						renderInput={(params) => (
							<Tooltip placement="top" title={params.inputProps.value}>
								<TextField
									variant="standard"
									disabled
									placeholder="Column"
									{...params}
									hiddenLabel
								/>
							</Tooltip>
						)}
						slotProps={{ popper: { style: { width: 'fit-content' } } }}
					/>
				</Stack>
			),
		}))
		return columns
	}, [columnMaps, excelData])

	return (
		<Dialog
			sx={{ minWidth: 900 }}
			fullWidth
			maxWidth="lg"
			open={open}
			onClose={() => onClose(null)}
			closeAfterTransition={false}
		>
			<DialogTitle onClose={() => onClose(null)}>File Layout Editor</DialogTitle>
			<DialogContent dividers>
				<Stack gap={2}>
					<ValueMappingButtons
						stringMapObject={stringMaps}
						setStringMaps={setStringMaps}
						columnMaps={columnMaps}
					/>
					<Paper>
						<DataGridPro
							sx={{
								'& .MuiDataGrid-columnHeaderTitleContainerContent': {
									height: '100%',
								},
								'& .MuiDataGrid-columnSeparator': {
									display: 'none',
								},
								minHeight: 200,
							}}
							loading={!rows.length || !categoryColumns.length}
							columnHeaderHeight={120}
							hideFooter
							disableVirtualization
							disableColumnMenu
							disableColumnSorting
							disableColumnResize
							disableColumnPinning
							disableColumnReorder
							disableRowSelectionOnClick
							density="compact"
							rows={rows}
							columns={[startRowColumn, ...categoryColumns]}
						/>
					</Paper>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={() => onClose(null)}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleSaveFileLayout}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	)
}
